import { useState, useCallback, useEffect, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import { Link, useNavigate } from "react-router-dom";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import CategoryModal from "../../components/CustomComponents/modal/parentCategoryModel";
import CustomerGroupSelectionModal from "../../components/CustomComponents/modal/customerGroupSelectionModel";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector } from "react-redux";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule";
import moment from "moment";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import { useProfileAndSettings } from "../../hooks/useProfileAndSettings";
import SaveActions from "../../components/CustomComponents/Buttons/SaveActions/SaveActions.jsx";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard.js";
import DiscountFormBody from "./DiscountFormBody";
import "./discountForm.css";

function AddDiscount() {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const { hasInitiallyLoaded } = useProfileAndSettings();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  
  const [compCurrency, setCompCurrency] = useState("GBP"); // Added state for CompCurrency
  const [discountData, setDiscountData] = useState({
    name: "",
    amountType: "Percentage (%)", // Always percentage
    amount: "",
    enableRules: false,
    quantity: "",
    enableMinQuantity: false,
    enableMinSpend: false,
    minSubtotal: "",
    enableMaxDiscount: false,
    maxDiscountValue: "",
    requirePasscode: false,
    applyDiscountAfterTaxes: false,
  });
  const [addAllItems, setAddAllItems] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showCustomerGroupModal, setShowCustomerGroupModal] = useState(false);
  const [selectedRule, setSelectedRule] = useState("Category");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState([]);
  const [orderCountCompareType, setOrderCountCompareType] = useState("Equals");
  const [orderCountThreshold, setOrderCountThreshold] = useState(0);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [enableSchedule, setEnableSchedule] = useState(false);
  const [enableDateRange, setEnableDateRange] = useState(false);
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [schedules, setSchedules] = useState(schedule);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    { from: "00:00", to: "23:59" },
  ]);
  const [dateRange, setDateRange] = useState({
    startDateTime: null,
    endDateTime: null,
  });

  const initialFormSnapshotRef = useRef(null);

  const serializeDate = useCallback((value) => {
    if (value == null || value === "") return null;
    const m = moment(value);
    return m.isValid() ? m.toISOString() : String(value);
  }, []);

  const getFormSnapshot = useCallback(() => {
    return JSON.stringify({
      discountData,
      addAllItems: Boolean(addAllItems),
      selectedRule,
      selectedProducts: (selectedProducts || []).map((p) => p?.menuItem_ID ?? p),
      selectedCategories: (selectedCategories || []).map(
        (c) => c?.rootCat_ID ?? c?.id ?? c
      ),
      selectedCustomerGroups: (selectedCustomerGroups || []).map(
        (g) => g?.cG_ID ?? g?.customerGroupID ?? g?.id ?? g
      ),
      orderCountCompareType,
      orderCountThreshold,
      enableSchedule: Boolean(enableSchedule),
      enableDateRange: Boolean(enableDateRange),
      useSameTimeSlots: Boolean(useSameTimeSlots),
      schedules,
      commonTimeSlot,
      dateRange: {
        startDateTime: serializeDate(dateRange.startDateTime),
        endDateTime: serializeDate(dateRange.endDateTime),
      },
    });
  }, [
    discountData,
    addAllItems,
    selectedRule,
    selectedProducts,
    selectedCategories,
    selectedCustomerGroups,
    orderCountCompareType,
    orderCountThreshold,
    enableSchedule,
    enableDateRange,
    useSameTimeSlots,
    schedules,
    commonTimeSlot,
    dateRange,
    serializeDate,
  ]);

  useEffect(() => {
    if (initialFormSnapshotRef.current === null) {
      initialFormSnapshotRef.current = getFormSnapshot();
    }
  }, [getFormSnapshot]);

  const isDirty =
    initialFormSnapshotRef.current !== null &&
    getFormSnapshot() !== initialFormSnapshotRef.current;

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    // Add more currency symbols as needed
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  // Fetch CompCurrency (only show error after initial load attempt; avoid toast while profile is still loading)
  const fetchCompCurrency = useCallback(async () => {
    try {
      if (!hasInitiallyLoaded) return;
      if (!profileData) {
        showErrorToast("Profile data not available. Please refresh the page.");
        return;
      }
      setCompCurrency(profileData?.compCurrency || "GBP");
    } catch (err) {
      showErrorToast("Failed to load company currency");
    }
  }, [profileData, hasInitiallyLoaded]);

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // Prevent negative values for the "amount" field
    if (name === "amount") {
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue < 0) {
        newValue = "0";
      }
    }

    setDiscountData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleToggleChange = (checked, name) => {
    setDiscountData((prev) => ({
      ...prev,
      [name]: checked,
      ...(name === "enableMinSpend" && !checked ? { minSubtotal: "" } : {}),
      ...(name === "enableMaxDiscount" && !checked ? { maxDiscountValue: "" } : {}),
      ...(name === "enableMinQuantity" && !checked ? { quantity: "" } : {}),
    }));
  };

  const getAmountPlaceholder = () => {
    return "Percentage (e.g. 10%)";
  };

  const fetchCategories = useCallback(async (options = {}) => {
    const showGlobalLoading = options.showGlobalLoading !== false;
    if (!CompID) return;
    try {
      if (showGlobalLoading) setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: "RootCat_Name",
          SortOrder: "ASC",
          Search: "",
        },
      });
      setCategories(response.data.data || response.data);
    } catch (err) {
      setCategories([]);
    } finally {
      if (showGlobalLoading) setLoading(false);
    }
  }, [CompID]);

  const fetchProducts = useCallback(async () => {
    if (!CompID) return;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID },
      });
      setProducts(response.data.data || response.data);
    } catch (err) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchCompCurrency(); // Added fetch for CompCurrency
  }, [fetchCategories, fetchProducts, fetchCompCurrency]);

  const handleProductSelection = (selectedProductItems) => {
    setSelectedProducts(selectedProductItems);
    setShowProductModal(false);
  };

  const handleRuleSelection = (rule) => {
    setSelectedRule(rule);
    if (rule === "Customer group") {
      setSelectedProducts([]);
      setSelectedCategories([]);
      setAddAllItems(false);
    } else {
      setDiscountData((prev) => ({
        ...prev,
        enableMinSpend: false,
        minSubtotal: "",
      }));
      setSelectedCustomerGroups([]);
    }
  };

  const handleCategorySelection = (selected) => {
    setSelectedCategories(selected || []);
    setShowCategoryModal(false);
  };

  const handleCustomerGroupSelection = (groups) => {
    setSelectedCustomerGroups(groups || []);
    setShowCustomerGroupModal(false);
  };

  const applyDiscountToRules = [
    { name: "Category", description: "Apply to specific categories (e.g., Holiday items)" },
    { name: "Customer group", description: "Apply to a customer group (e.g., Regulars)" },
    {
      name: "Order count",
      description: "Apply when the customer’s completed order count matches Equals or At most",
    },
  ];

  const removeProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.filter((p) => p.menuItem_ID !== productId)
    );
  };

  const removeCategory = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.filter((c) => c.rootCat_ID !== categoryId)
    );
  };

  const removeCustomerGroup = (groupId) => {
    setSelectedCustomerGroups((prev) => prev.filter((g) => g.id !== groupId));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange((prev) => {
      const momentValue = value && moment.isMoment(value) && value.isValid() ? value : null;
      const newDateRange = { ...prev, [field]: momentValue };

      if (
        field === "startDateTime" &&
        newDateRange.endDateTime &&
        moment.isMoment(newDateRange.endDateTime) &&
        newDateRange.endDateTime.isValid()
      ) {
        if (newDateRange.endDateTime.isBefore(momentValue)) {
          newDateRange.endDateTime = momentValue ? momentValue.clone() : null;
        }
      }

      return newDateRange;
    });
  };

  // Note: These validation functions are no longer used since we removed isValidDate props
  // The CombinedDateTimePicker now uses its built-in default validation

  const handleEnableDateRangeToggle = (checked) => {
    if (checked) {
      const now = moment();
      setDateRange({
        startDateTime: now,
        endDateTime: now,
      });
    } else {
      setDateRange({
        startDateTime: null,
        endDateTime: null,
      });
    }
    setEnableDateRange(checked);
  };

  const handleSubmit = async (options = {}) => {
    const { exitAfterSave = true } = options;

    if (!discountData.name.trim()) {
      showErrorToast("Discount name is required");
      return false;
    }

    if (!discountData.amount || parseFloat(discountData.amount) < 0 || isNaN(parseFloat(discountData.amount))) {
      showErrorToast("Valid non-negative discount amount is required");
      return false;
    }

    const amountNum = parseFloat(discountData.amount);
    if (amountNum > 100) {
      showErrorToast("Percentage cannot exceed 100%");
      return false;
    }

    if (enableDateRange) {
      if (!dateRange.startDateTime || !moment(dateRange.startDateTime).isValid()) {
        showErrorToast("Please select a valid start date and time");
        return false;
      }
      if (!dateRange.endDateTime || !moment(dateRange.endDateTime).isValid()) {
        showErrorToast("Please select a valid end date and time");
        return false;
      }
      if (moment(dateRange.endDateTime).isBefore(dateRange.startDateTime)) {
        showErrorToast("End date and time cannot be before start date and time");
        return false;
      }
    }

    if (discountData.enableMaxDiscount && !discountData.maxDiscountValue) {
      showErrorToast("Maximum discount value is required when enabled");
      return false;
    }

    if (discountData.enableMinQuantity) {
      const qtyNum = parseInt(discountData.quantity, 10);
      if (discountData.quantity === "" || isNaN(qtyNum) || qtyNum < 1) {
        showErrorToast("Valid minimum quantity is required when enabled");
        return false;
      }
    }

    if (discountData.enableMinSpend) {
      const minSpendNum = parseFloat(discountData.minSubtotal);
      if (discountData.minSubtotal === "" || isNaN(minSpendNum) || minSpendNum < 0) {
        showErrorToast("Valid minimum spend amount is required when enabled");
        return false;
      }
    }

    if (discountData.enableRules) {
      if (selectedRule === "Category") {
        if (!addAllItems && selectedCategories.length === 0 && selectedProducts.length === 0) {
          showErrorToast("Select at least one category or product when discount rules are enabled");
          return false;
        }
      } else if (selectedRule === "Customer group") {
        if (selectedCustomerGroups.length === 0) {
          showErrorToast("Select at least one customer group");
          return false;
        }
      } else if (selectedRule === "Order count") {
        if (
          orderCountThreshold === "" ||
          orderCountThreshold === null ||
          Number.isNaN(Number(orderCountThreshold)) ||
          Number(orderCountThreshold) < 0
        ) {
          showErrorToast("Enter a valid number of orders (0 or greater)");
          return false;
        }
        if (
          orderCountCompareType !== "Equals" &&
          orderCountCompareType !== "AtMost"
        ) {
          showErrorToast("Select Equals or At most for the order count rule");
          return false;
        }
      }
    }

    if (enableSchedule) {
      const hasCommonSlot = useSameTimeSlots && commonTimeSlot.length > 0 && commonTimeSlot.every(
        (slot) => moment(slot.from, ["h:mm A"]).isValid() && moment(slot.to, ["h:mm A"]).isValid()
      );
      const hasPerDaySlot = !useSameTimeSlots && schedules.some(
        (s) => s.checked && s.timeSlots.length > 0 && s.timeSlots.every(
          (slot) => moment(slot.from, ["h:mm A"]).isValid() && moment(slot.to, ["h:mm A"]).isValid()
        )
      );
      if (!hasCommonSlot && !hasPerDaySlot) {
        showErrorToast("Add at least one time slot when schedule is enabled");
        return false;
      }
    }

    setLoading(true);

    try {
      let startDateTime = null;
      let endDateTime = null;
      if (enableDateRange) {
        if (dateRange.startDateTime && moment(dateRange.startDateTime).isValid()) {
          startDateTime = moment(dateRange.startDateTime).format("YYYY-MM-DDTHH:mm:ss");
        }
        if (dateRange.endDateTime && moment(dateRange.endDateTime).isValid()) {
          endDateTime = moment(dateRange.endDateTime).format("YYYY-MM-DDTHH:mm:ss");
        }
      }

      const formattedSchedules = [];
      if (enableSchedule) {
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                formattedSchedules.push({
                  ID: 0,
                  CompID: CompID,
                  DiscountID: 0,
                  DayName: day,
                  OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                  CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                  UpdateDateTime: new Date().toISOString(),
                });
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    ID: 0,
                    CompID: CompID,
                    DiscountID: 0,
                    DayName: schedule.day,
                    OpenTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    CloseTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    UpdateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
      }

      const discountPayload = {
        ID: 0,
        CompID: CompID,
        DiscountName: discountData.name,
        AmountType: "Percentage", // Always percentage
        DiscountPercentage: parseFloat(discountData.amount) || 0,
        OrderAmount: 0, // Always 0 since we only use percentage
        IsDiscountRulesActive: discountData.enableRules,
        DiscountRuleType: selectedRule,
        DiscountApplyWhenQTYofItems: discountData.enableMinQuantity
          ? parseInt(discountData.quantity) || 0
          : 0,
        IsDiscountApplyOnAllItems: addAllItems,
        IsSetMinimumSpend: discountData.enableMinSpend,
        MinimumSpendAmount: discountData.enableMinSpend
          ? parseFloat(discountData.minSubtotal) || 0
          : 0,
        IsSetMaxDiscountValue: discountData.enableMaxDiscount,
        MaxDiscountValue: discountData.enableMaxDiscount
          ? parseFloat(discountData.maxDiscountValue) || 0
          : null,
        IsRequirePassCode: discountData.requirePasscode,
        IsDiscountafterTax: discountData.applyDiscountAfterTaxes,
        IsScheduleDiscount: enableSchedule,
        IsSetDateRange: enableDateRange,
        DiscountStartDate:
          enableDateRange && startDateTime ? startDateTime : null,
        DiscountEndDate: enableDateRange && endDateTime ? endDateTime : null,
        IsAutoApplyonPOS: false,
        IsAutoApplyonOnlineSites: selectedRule === "Order count",
        OrderCountThreshold:
          discountData.enableRules && selectedRule === "Order count"
            ? parseInt(orderCountThreshold, 10) || 0
            : 0,
        OrderCountCompareType:
          discountData.enableRules && selectedRule === "Order count"
            ? orderCountCompareType || "Equals"
            : "Equals",
        UpdateDateTime: new Date().toISOString(),
        DiscountWeeklyTimings: formattedSchedules,
        DiscountEntryItems:
          discountData.enableRules &&
          selectedRule === "Category" &&
          !addAllItems
            ? selectedProducts
            : [],
        DiscountEntryCategories:
          discountData.enableRules &&
          selectedRule === "Category" &&
          !addAllItems
            ? selectedCategories
            : [],
        DiscountEntryCustomerGroups:
          discountData.enableRules && selectedRule === "Customer group"
            ? selectedCustomerGroups
            : [],
      };

      const response = await axiosInstance.post(
        APIs.POST_ADD_DISCOUNT,
        discountPayload
      );

      if (!response.data.status) {
        showErrorToast(response.data.Message || "Failed to add discount");
        return false;
      }

      showSuccessToast("Discount added successfully");
      initialFormSnapshotRef.current = getFormSnapshot();
      if (exitAfterSave) {
        navigate("/discount/manageDiscount");
      }
      return true;
    } catch (error) {
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.errors?.join(", ") ||
        "An error occurred while adding the discount";
      showErrorToast(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  useUnsavedChangesGuard({
    isDirty,
    onSave: () => handleSubmit({ exitAfterSave: false }),
    onDiscard: () => {
      initialFormSnapshotRef.current = getFormSnapshot();
    },
  });

  return (
    <div className="discount-form-page" style={{ "--df-accent": themeColor }}>
      <div className="discount-form-page__head">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/discount/manageDiscount">Manage discounts</Link>
            </li>
            <li className="breadcrumb-item active">Create discount</li>
          </ol>
          <h1 className="discount-form-page__title">Create discount</h1>
          <p className="discount-form-page__sub">
            Define percentage, rules, schedule, and checkout options.
          </p>
        </div>
        <SaveActions
          onCancel={() => navigate("/discount/manageDiscount")}
          onSave={() => handleSubmit({ exitAfterSave: false })}
          onSaveAndExit={() => handleSubmit({ exitAfterSave: true })}
          saving={loading}
        />
      </div>

      <GridSectionLoader loading={loading} minHeight="280px">
        <DiscountFormBody
          themeColor={themeColor}
          isDarkMode={isDarkMode}
          discountData={discountData}
          handleInputChange={handleInputChange}
          handleToggleChange={handleToggleChange}
          getAmountPlaceholder={getAmountPlaceholder}
          getCurrencySymbol={getCurrencySymbol}
          compCurrency={compCurrency}
          applyDiscountToRules={applyDiscountToRules}
          selectedRule={selectedRule}
          handleRuleSelection={handleRuleSelection}
          addAllItems={addAllItems}
          selectedCategories={selectedCategories}
          selectedProducts={selectedProducts}
          selectedCustomerGroups={selectedCustomerGroups}
          removeCategory={removeCategory}
          removeProduct={removeProduct}
          removeCustomerGroup={removeCustomerGroup}
          setShowCategoryModal={setShowCategoryModal}
          setShowCustomerGroupModal={setShowCustomerGroupModal}
          orderCountCompareType={orderCountCompareType}
          setOrderCountCompareType={setOrderCountCompareType}
          orderCountThreshold={orderCountThreshold}
          setOrderCountThreshold={setOrderCountThreshold}
          enableSchedule={enableSchedule}
          setEnableSchedule={setEnableSchedule}
          useSameTimeSlots={useSameTimeSlots}
          setUseSameTimeSlots={setUseSameTimeSlots}
          applyCommonTimeSlotToAll={applyCommonTimeSlotToAll}
          commonTimeSlot={commonTimeSlot}
          setCommonTimeSlot={setCommonTimeSlot}
          schedules={schedules}
          setSchedules={setSchedules}
          enableDateRange={enableDateRange}
          handleEnableDateRangeToggle={handleEnableDateRangeToggle}
          dateRange={dateRange}
          handleDateRangeChange={handleDateRangeChange}
        />
      </GridSectionLoader>

      <ProductMultiSelectionModal
        showModal={showProductModal}
        setShowModal={setShowProductModal}
        showButton={true}
        onSubmit={handleProductSelection}
        setSelectedProducts={setSelectedProducts}
        selectedProducts={selectedProducts}
        products={products}
      />

      <CategoryModal
        showParentCategoryModal={showCategoryModal}
        setShowParentCategoryModal={setShowCategoryModal}
        showButton={true}
        onSubmit={handleCategorySelection}
        setSelectedParentCategory={setSelectedCategories}
        selectedParentCategory={selectedCategories}
        categories={categories}
        isMultiSelect={true}
        modalTitle="Select Category"
        modalSubTitle="Select the categories for the discount"
        fetchCategories={fetchCategories}
      />

      <CustomerGroupSelectionModal
        showModal={showCustomerGroupModal}
        setShowModal={setShowCustomerGroupModal}
        onSubmit={handleCustomerGroupSelection}
        selectedGroups={selectedCustomerGroups}
        setSelectedGroups={setSelectedCustomerGroups}
        subtitle="Select customer groups to apply the discount"
      />
    </div>
  );
}

export default AddDiscount;